import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { SubscriptionData, SubscriptionState } from '../slices/subscriptionSlice';

// Base selector for subscription state
export const selectSubscriptionState = (state: RootState): SubscriptionState => state.subscription;

// Basic selectors
export const selectSubscriptionData = (state: RootState): SubscriptionData | null => 
    state.subscription.data;

export const selectCustomerSessionSecret = (state: RootState): string | null =>
    state.subscription.data?.customerSessionSecret || null;

export const selectSubscriptionLoading = (state: RootState): boolean => 
    state.subscription.loading;

export const selectSubscriptionError = (state: RootState): string | null => 
    state.subscription.error;

export const selectShowPlans = (state: RootState): boolean => 
    state.subscription.showPlans;

export const selectLastFetched = (state: RootState): number | null => 
    state.subscription.lastFetched;

// Derived selectors using createSelector for memoization
export const selectHasActiveSubscription = createSelector(
    [selectSubscriptionData],
    (data: SubscriptionData | null): boolean => {
        if (!data || !data.subscriptions) return false;
        return data.subscriptions.some(sub => 
            sub.status === 'active' || sub.status === 'trialing'
        );
    }
);

export const selectActiveSubscription = createSelector(
    [selectSubscriptionData],
    (data: SubscriptionData | null) => {
        if (!data || !data.subscriptions) return null;
        return data.subscriptions.find(sub => 
            sub.status === 'active' || sub.status === 'trialing'
        ) || null;
    }
);

export const selectSubscriptionStatus = createSelector(
    [selectActiveSubscription],
    (activeSubscription) => {
        if (!activeSubscription) return 'none';
        return activeSubscription.status;
    }
);

export const selectSubscriptionPlanName = createSelector(
    [selectActiveSubscription],
    (activeSubscription) => {
        if (!activeSubscription) return null;
        return activeSubscription.items?.data[0]?.price?.nickname || 'Pro Plan';
    }
);

export const selectSubscriptionPlanId = createSelector(
    [selectActiveSubscription],
    (activeSubscription) => {
        if (!activeSubscription) return null;
        return activeSubscription.items?.data[0]?.price?.id || null;
    }
);

export const selectNextBillingDate = createSelector(
    [selectActiveSubscription],
    (activeSubscription) => {
        if (!activeSubscription || !activeSubscription.current_period_end) return null;
        return new Date(activeSubscription.current_period_end * 1000);
    }
);

export const selectFormattedNextBillingDate = createSelector(
    [selectNextBillingDate],
    (date: Date | null): string | null => {
        if (!date) return null;
        return date.toLocaleDateString();
    }
);

export const selectCancelAtPeriodEnd = createSelector(
    [selectActiveSubscription],
    (activeSubscription) => {
        if (!activeSubscription) return false;
        return activeSubscription.cancel_at_period_end || false;
    }
);

export const selectSubscriptionAmount = createSelector(
    [selectActiveSubscription],
    (activeSubscription) => {
        if (!activeSubscription) return null;
        const amount = activeSubscription.items?.data[0]?.price?.unit_amount;
        const currency = activeSubscription.items?.data[0]?.price?.currency;
        
        if (!amount || !currency) return null;
        
        return {
            amount,
            currency,
            formatted: new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency.toUpperCase(),
            }).format(amount / 100)
        };
    }
);

export const selectSubscriptionInterval = createSelector(
    [selectActiveSubscription],
    (activeSubscription) => {
        if (!activeSubscription) return null;
        return activeSubscription.items?.data[0]?.price?.recurring?.interval || null;
    }
);

// Selector to check if subscription data is stale (older than 5 minutes)
export const selectIsSubscriptionDataStale = createSelector(
    [selectLastFetched],
    (lastFetched: number | null): boolean => {
        if (!lastFetched) return true;
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        return lastFetched < fiveMinutesAgo;
    }
);

export const selectCancelledSubscriptions = createSelector(
    [selectSubscriptionData],
    (data: SubscriptionData | null) => {
        if (!data || !data.subscriptions) return [];
        return data.subscriptions.filter(sub => 
            sub.status === 'canceled' || 
            sub.status === 'incomplete_expired' || 
            sub.status === 'unpaid'
        );
    }
);

export const selectHasCancelledSubscriptions = createSelector(
    [selectCancelledSubscriptions],
    (cancelledSubscriptions) => {
        return cancelledSubscriptions.length > 0;
    }
);

// Composite selector for subscription summary
export const selectSubscriptionSummary = createSelector(
    [
        selectHasActiveSubscription,
        selectSubscriptionStatus,
        selectSubscriptionPlanName,
        selectFormattedNextBillingDate,
        selectCancelAtPeriodEnd,
        selectSubscriptionAmount,
        selectSubscriptionInterval,
        selectSubscriptionLoading,
        selectSubscriptionError
    ],
    (
        hasActive,
        status,
        planName,
        nextBillingDate,
        cancelAtPeriodEnd,
        amount,
        interval,
        loading,
        error
    ) => ({
        hasActiveSubscription: hasActive,
        status,
        planName,
        nextBillingDate,
        cancelAtPeriodEnd,
        amount,
        interval,
        loading,
        error
    })
); 